import React, { useState, useEffect, useRef } from "react";
import "./Recruiting.css";
import logo from "./assets/dg-logo.png";
import userIcon from "./assets/logout.png";
import Navigation from "./Navigation";
import { fetchWithAuth } from "./utils";
import { useToast } from './ToastContext';
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface Region {
  company_code: string;
  region_code: string;
  region_name: string;
}

interface RecruitingRequest {
  recruiting_requests_id: number;
  recruiter_name: string;
  office_code: string;
  role_name: string;
  hiring_manager: string;
  requisition_id: string | null;
  candidate_name: string | null;
  status: string | null;
  discipline: string;
  notes: string | null;
}


const Recruiting: React.FC = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);
  const [selectedSubLink, setSelectedSubLink] = useState<
    "manning" | "opportunity" | "project" | "openpros" | "recruiting"
  >("recruiting");
  const [username, setUsername] = useState("");
  const [showLogout, setShowLogout] = useState(false);
  const logoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { showToast } = useToast();
  const [recruitingRequests, setRecruitingRequests] = useState<RecruitingRequest[]>([]);
  const [isLoadingRecruiting, setIsLoadingRecruiting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedDiscipline, setSelectedDiscipline] = useState<string>("CSI");
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [editingRecruitingId, setEditingRecruitingId] = useState<number | null>(null);


  const [newRecruiting, setNewRecruiting] = useState({
    recruiter_name: "",
    office_code: "",
    role_name: "",
    hiring_manager: "",
    requisition_id: "",
    candidate_name: "",
    status: "OPEN",
    discipline: "CSI",
    notes: "",
  });

  const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
  };

  const FIXED_DISCIPLINES = [
    { discipline_code: "CSI", discipline_name: "CSI" },
  { discipline_code: "IS", discipline_name: "IS" },
  { discipline_code: "CONSTRUCTION", discipline_name: "Construction" },
  { discipline_code: "ELECTRICAL", discipline_name: "Electrical" },
  { discipline_code: "MECHANICAL", discipline_name: "Mechanical" },
 ];

  const validateRecruitingForm = () => {
    const newErrors: Record<string, string> = {};

    if (!newRecruiting.recruiter_name.trim()) {
      newErrors.recruiter_name = "Recruiter name is required.";
    }

    if (!newRecruiting.office_code.trim()) {
      newErrors.office_code = "Office code is required.";
    }

    if (!newRecruiting.role_name.trim()) {
      newErrors.role_name = "Role name is required.";
    }

    if (!newRecruiting.hiring_manager.trim()) {
      newErrors.hiring_manager = "Hiring manager is required.";
    }

    if (!newRecruiting.status) {
      newErrors.status = "Status is required.";
    }

    if (!newRecruiting.discipline) {
      newErrors.discipline = "Discipline is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };



  // === Fetch regions ===
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const cached = localStorage.getItem('regions_new');
        if (cached) {
          setRegions(JSON.parse(cached));
        } else {
          const url =
            'https://manningserviceapp.azurewebsites.net/api/MainRoute?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==&action=get_regions&company_code=DG';
          const data = await fetchWithAuth(url);
          setRegions(data);
          localStorage.setItem('regions_new', JSON.stringify(data));
        }
      } catch (error) {
        console.error('Error fetching regions:', error);
        showToast('Error fetching regions.', 'error');
      }
    };
    fetchRegions();
  }, []);

  // === Session user ===
  useEffect(() => {
    const sessionData = localStorage.getItem("userToken");
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        setUsername(parsed.username || "");
      } catch (error) {
        console.error("Failed to parse session userToken:", error);
      }
    }
  }, []);

useEffect(() => {
  const fetchRecruitingRequests = async () => {
    try {
      setIsLoadingRecruiting(true);

      const url =
        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
        "&action=get_all_recruiting_requests" +
        `&discipline_code=${selectedDiscipline}`;

      const response = await fetchWithAuth(url);

      if (response?.status === "success") {
        setRecruitingRequests(response.message.records || []);
      } else {
        showToast("Failed to load recruiting requests.", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Error loading recruiting requests.", "error");
    } finally {
      setIsLoadingRecruiting(false);
    }
  };

  fetchRecruitingRequests();
}, [selectedDiscipline]);


const resetRecruitingForm = () => {
  setNewRecruiting({
    recruiter_name: "",
    office_code: "",
    role_name: "",
    hiring_manager: "",
    requisition_id: "",
    candidate_name: "",
    status: "OPEN",
    discipline: "CSI",
    notes: "",
  });

  setErrors({});
};

const stripHtml = (html: string) => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const truncateText = (text: string, length = 7) => {
  if (!text) return "—";
  return text.length > length ? `${text.slice(0, length)}…` : text;
};


  // === Hover behavior for logout ===
  const handleMouseEnter = () => {
    if (logoutTimeoutRef.current) clearTimeout(logoutTimeoutRef.current);
    setShowLogout(true);
  };

  const handleMouseLeave = () => {
    logoutTimeoutRef.current = setTimeout(() => {
      setShowLogout(false);
    }, 200);
  };

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    window.location.href = "/";
  };

  // === Expand / collapse region in Navigation ===
  const toggleRegion = (regionCode: string) => {
    setExpandedRegion((prev) => (prev === regionCode ? null : regionCode));
    setSelectedSubLink("recruiting");
  };

  return (
    <div className="landing-container">
      {/* === HEADER === */}
      <header className="landing-header">
        <img src={logo} alt="Design Group Logo" className="landing-logo" />
        <h1 className="landing-title">Staffing Portal - Recruiting</h1>

        <div
          className="user-icon-container"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span className="username-text">{username}</span>
          <img src={userIcon} alt="User Icon" className="user-icon" />
          {showLogout && (
            <button className="logout-button" onClick={handleLogout}>
              Logout
            </button>
          )}
        </div>
      </header>

      {/* === SIDE NAVIGATION === */}
      <Navigation
        regions={regions}
        expandedRegion={expandedRegion}
        selectedSubLink={selectedSubLink}
        toggleRegion={toggleRegion}
        setSelectedSubLink={setSelectedSubLink}
      />

      {/* === MAIN BODY === */}
      <main className="landing-main">
        <div className="recruiting-container">
          {/* === Example Page Layout === */}
          <div className="recruiting-header-row">
            <h2 className="section-header">
              Recruiting Requests ({selectedDiscipline})
            </h2>

            <select
              className="discipline-filter"
              value={selectedDiscipline}
              onChange={(e) => setSelectedDiscipline(e.target.value)}
            >
              {FIXED_DISCIPLINES.map((d) => (
                <option key={d.discipline_code} value={d.discipline_code}>
                  {d.discipline_name}
                </option>
              ))}
            </select>
          </div>

          <div className="recruiting-header-actions">
            <button
              className="button_w200 primary"
              onClick={() => {
                setModalMode("add");
                resetRecruitingForm();
                setShowAddModal(true);
              }}
            >
              ➕ Add New Recruitment
            </button>
          </div>

          {isLoadingRecruiting ? (
            <p>Loading recruiting requests...</p>
          ) : recruitingRequests.length === 0 ? (
            <p>No recruiting requests found.</p>
          ) : (
            <div className="recruiting-table-wrapper">
              <table className="recruiting-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Recruiter</th>
                    <th>Office</th>
                    <th>Role</th>
                    <th>Hiring Manager</th>
                    <th>Requisition ID</th>
                    <th>Candidate</th>
                    <th>Status</th>
                    <th>Discipline</th>
                    <th>Notes</th>
                  </tr>
                </thead>

                <tbody>
                  {recruitingRequests.map((req, index) => (
                    <tr key={req.recruiting_requests_id}>
                      <td>
                        <button
                          className="link-button"
                          onClick={() => {
                            setModalMode("edit");
                            setEditingRecruitingId(req.recruiting_requests_id);

                            setNewRecruiting({
                              recruiter_name: req.recruiter_name,
                              office_code: req.office_code,
                              role_name: req.role_name,
                              hiring_manager: req.hiring_manager,
                              requisition_id: req.requisition_id ?? "",
                              candidate_name: req.candidate_name ?? "",
                              status: req.status ?? "OPEN",
                              discipline: req.discipline,
                              notes: req.notes ?? "",
                            });

                            setErrors({});
                            setShowAddModal(true);
                          }}
                        >
                          {index + 1}
                        </button>
                      </td>

                      <td>{req.recruiter_name}</td>
                      <td>{req.office_code}</td>
                      <td>{req.role_name}</td>
                      <td>{req.hiring_manager}</td>
                      <td>{req.requisition_id ?? "—"}</td>
                      <td>{req.candidate_name ?? "—"}</td>
                      <td>{req.status ?? "—"}</td>
                      <td>{req.discipline}</td>
                      <td>
                        {req.notes ? (
                          <span
                            className="notes-preview"
                            title={stripHtml(req.notes)}   // native tooltip
                          >
                            {truncateText(stripHtml(req.notes), 7)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>

              </table>
            </div>
          )}
         </div>

        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal">
              <h3>
                {modalMode === "add" ? "Add New Recruitment" : "Edit Recruitment"}
              </h3>


              <div className="modal-grid">
                <input
                  placeholder="Recruiter Name"
                  value={newRecruiting.recruiter_name}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, recruiter_name: e.target.value });
                    setErrors({ ...errors, recruiter_name: "" });
                  }}
                />
                {errors.recruiter_name && (
                  <span className="field-error">{errors.recruiter_name}</span>
                )}

                <input
                  placeholder="Office Code"
                  value={newRecruiting.office_code}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, office_code: e.target.value });
                    setErrors({ ...errors, office_code: "" });
                  }}
                />
                {errors.office_code && (
                  <span className="field-error">{errors.office_code}</span>
                )}

                <input
                  placeholder="Role Name"
                  value={newRecruiting.role_name}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, role_name: e.target.value });
                    setErrors({ ...errors, role_name: "" });
                  }}
                />
                {errors.role_name && (
                  <span className="field-error">{errors.role_name}</span>
                )}

                <input
                  placeholder="Hiring Manager"
                  value={newRecruiting.hiring_manager}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, hiring_manager: e.target.value });
                    setErrors({ ...errors, hiring_manager: "" });
                  }}
                />
                {errors.hiring_manager && (
                  <span className="field-error">{errors.hiring_manager}</span>
                )}


                <input
                  placeholder="Requisition ID"
                  value={newRecruiting.requisition_id}
                  onChange={(e) =>
                    setNewRecruiting({ ...newRecruiting, requisition_id: e.target.value })
                  }
                />

                <input
                  placeholder="Candidate Name"
                  value={newRecruiting.candidate_name}
                  onChange={(e) =>
                    setNewRecruiting({ ...newRecruiting, candidate_name: e.target.value })
                  }
                />

                <select
                  value={newRecruiting.discipline}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, discipline: e.target.value });
                    setErrors({ ...errors, discipline: "" });
                  }}
                >
                  <option value="">Select Discipline *</option>
                  {FIXED_DISCIPLINES.map((d) => (
                    <option key={d.discipline_code} value={d.discipline_code}>
                      {d.discipline_name}
                    </option>
                  ))}
                </select>

                {errors.discipline && (
                  <span className="field-error">{errors.discipline}</span>
                )}

                <select
                  value={newRecruiting.status}
                  onChange={(e) => {
                    setNewRecruiting({ ...newRecruiting, status: e.target.value });
                    setErrors({ ...errors, status: "" });
                  }}
                >
                  <option value="">Select Status *</option> 
                  <option value="OPEN">OPEN</option>
                  <option value="INTERVIEW_IN_PROGRESS">INTERVIEW_IN_PROGRESS</option>
                  <option value="OFFER_EXTENDED">OFFER_EXTENDED</option>
                  <option value="OFFER_VERBAL_ACCEPTED">OFFER_VERBAL_ACCEPTED</option>
                  <option value="DG_APPROVAL_IN_PROGRESS">DG_APPROVAL_IN_PROGRESS</option>
                  <option value="OFFER_ACCEPTED">OFFER_ACCEPTED</option>
                  <option value="ON_HOLD">ON_HOLD</option>
                  <option value="CLOSED">CLOSED</option>
                </select>
                {errors.status && <span className="field-error">{errors.status}</span>}

                <div className="quill-wrapper">
                  <label className="modal-label">Notes</label>
                  <ReactQuill
                    theme="snow"
                    modules={quillModules}
                    value={newRecruiting.notes}
                    onChange={(value) =>
                      setNewRecruiting({ ...newRecruiting, notes: value })
                    }
                    placeholder="Enter recruiting notes..."
                  />
                </div>

              </div>

              <div className="modal-actions">
                <button
                  className="button_w100 secondary"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="button_w100 primary"
                  onClick={async () => {
                    if (!validateRecruitingForm()) {
                      showToast("Please fix the required fields.", "error");
                      return;
                    }

                    try {
                      const action =
                        modalMode === "add"
                          ? "add_recruiting_request"
                          : "update_recruiting_request";

                      const payload =
                        modalMode === "add"
                          ? newRecruiting
                          : {
                              recruiting_requests_id: editingRecruitingId,
                              ...newRecruiting,
                            };

                      const url =
                        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
                        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
                        `&action=${action}`;

                      await fetchWithAuth(url, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                      });

                      showToast(
                        modalMode === "add"
                          ? "Recruiting request added successfully."
                          : "Recruiting request updated successfully.",
                        "success"
                      );

                      setShowAddModal(false);
                      setModalMode("add");
                      setEditingRecruitingId(null);
                      resetRecruitingForm();

                      // Refresh list
                      const refreshUrl =
                        "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
                        "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
                        "&action=get_all_recruiting_requests" +
                        `&discipline=${selectedDiscipline}`;

                      const refreshed = await fetchWithAuth(refreshUrl);
                      setRecruitingRequests(refreshed.message.records || []);
                    } catch (err) {
                      console.error(err);
                      showToast("Error saving recruiting request.", "error");
                    }
                  }}
                >
                  Save
                </button>

              </div>
            </div>
          </div>
        )}

      </main>
    </div>
  );
};

export default Recruiting;
